import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { onEnterKey } from "@songtradr-one/components-frontend";
import { appModulesList, routes } from "@songtradr-one/shared-common";
import { useAppContext } from "@songtradr-one/shared-frontend";
import {
  Container,
  Props,
  TilesContainer,
  Title,
  moduleImageByKey,
  text,
} from "./ControlCenter.Parts";
import {
  getMaxSmallColumnCount,
  getMinLargeColumnCount,
  getMinMediumColumnCount,
} from "./ControlCenter.Parts";
import { Tile } from "./tile/Tile";

export const ControlCenter = ({ arrowKeyNavigation }: Props) => {
  const [searchParams] = useSearchParams();
  const { hasAnyPermission, hasAllPermissions } = useAppContext();
  const location = useLocation();
  const navigate = useNavigate();

  const dashboardModules = appModulesList
    .filter(
      (
        module,
      ): module is typeof module & {
        dashboardOrder: number;
        baseRoute: string;
      } =>
        module.dashboardOrder !== undefined &&
        module.baseRoute !== undefined &&
        (module.match === "AnyPermission"
          ? hasAnyPermission(module.permissions)
          : hasAllPermissions(module.permissions)),
    )
    .sort((a, b) => a.dashboardOrder - b.dashboardOrder);

  const maxSmallColumns = getMaxSmallColumnCount(dashboardModules.length);
  const minMediumColumns = getMinMediumColumnCount(dashboardModules.length);
  const minLargeColumns = getMinLargeColumnCount(dashboardModules.length);

  return (
    <Container>
      <Title>{text.title}</Title>
      <TilesContainer
        $maxSmallColumns={maxSmallColumns}
        $maxMediumColumns={minMediumColumns}
        $minLargeColumns={minLargeColumns}
      >
        {dashboardModules.map((module, index) => {
          const ModuleImage = moduleImageByKey[module.key] ?? undefined;

          // Accessibility props using roving tabindex for arrow key navigation when rendered within a floating-ui portal
          const navigationProps = arrowKeyNavigation
            ? arrowKeyNavigation.getItemProps({
                ref: (node: HTMLElement | null) => {
                  arrowKeyNavigation.listRef.current[index] = node;
                },
                tabIndex: arrowKeyNavigation.activeIndex === index ? 0 : -1,
              })
            : {};

          return (
            <Tile
              route={module.route ?? module.baseRoute}
              key={module.key}
              title={module.label}
              lottieAnimation={ModuleImage}
              disabled={location.pathname.includes(module.baseRoute)}
              active={
                searchParams
                  .toString()
                  .includes(module.baseRoute.replace("?", "")) &&
                module.baseRoute.startsWith(routes.portal.drawer)
              }
              onKeyDown={onEnterKey<Element>(() => {
                navigate(module.route ?? module.baseRoute);
              })}
              {...navigationProps}
            />
          );
        })}
      </TilesContainer>
    </Container>
  );
};
